import { MessagePrimitive, ReasoningContentPartComponent } from '@assistant-ui/react';
import { ComponentPropsWithoutRef, FC, forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { ContentPart, useThreadConfig } from '@assistant-ui/react-ui';
import { withDefaults } from './utils/with-defaults';
import { AssistantAvatar } from './assistant-avatar';
import { ChevronDownIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { cn } from '@shadcn/ui/lib/utils';

const AssistantMessage: FC = () => {
    return (
        <AssistantMessageRoot>
            {/* <AssistantMessageAvatar /> */}
            <AssistantMessageContent />
        </AssistantMessageRoot>
    );
};

AssistantMessage.displayName = 'AssistantMessage';

const AssistantMessageRoot = withDefaults(MessagePrimitive.Root, {
    className: 'aui-assistant-message-root w-full max-w-[calc(var(--thread-max-width)*0.85)]',
});

const AssistantMessageAvatar: FC = () => {
    return <AssistantAvatar />;
};

AssistantMessageRoot.displayName = 'AssistantMessageRoot';

const AssistantMessageContentWrapper = withDefaults('div', {
    className:
        'aui-assistant-message-content w-full max-w-[calc(var(--thread-max-width)*0.85)] px-2 py-2 animate-fade-in-0.5',
});

export const ReasoningPreview: ReasoningContentPartComponent = ({ text, status }) => {
    const [isCollapsed, setIsCollapsed] = useState(status.type !== 'running');
    const [prevText, setPrevText] = useState('');
    const [renderedDeltas, setRenderedDeltas] = useState<string[]>([]);
    const deltaQueue = useRef<string[]>([]);
    const animating = useRef(false);
    const isRunning = status.type === 'running';

    // Collapse logic
    useEffect(() => {
        if (status.type === 'complete' && !isCollapsed) {
            setIsCollapsed(true);
        } else if (isRunning) {
            setIsCollapsed(false);
        }
    }, [status.type]);

    const getThinkingState = () => {
        if (isRunning) return 'Planning...';
        if (status.type === 'complete') {
            return 'Planning finished';
        }
        if (status.type === 'incomplete') return `Planning Incomplete: ${status.reason}`;
        if (status.type === 'requires-action') return `Action Required: ${status.reason}`;
        return 'Planning...';
    };

    // Detect new delta(s) and queue them
    useEffect(() => {
        if (!text.startsWith(prevText)) {
            // Reset if text is replaced or cleared
            setPrevText(text);
            setRenderedDeltas([]);
            deltaQueue.current = [];
            animating.current = false;
            return;
        }
        if (text !== prevText) {
            const newDelta = text.slice(prevText.length);
            if (newDelta.length > 0) {
                deltaQueue.current.push(newDelta);
                processQueue();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [text, prevText]);

    // Process the queue of deltas
    const processQueue = () => {
        if (animating.current) return;
        if (deltaQueue.current.length === 0) return;
        animating.current = true;
        const nextDelta = deltaQueue.current.shift()!;
        setRenderedDeltas((prev) => [...prev, nextDelta]);
        setTimeout(() => {
            setPrevText((prev) => prev + nextDelta);
            animating.current = false;
            if (deltaQueue.current.length > 0) {
                processQueue();
            }
        }, 200); // 0.5s = 500ms
    };

    // If prevText is reset, clear queue and deltas
    useEffect(() => {
        if (!text.startsWith(prevText)) {
            setRenderedDeltas([]);
            deltaQueue.current = [];
            animating.current = false;
        }
    }, [text, prevText]);

    return (
        <div className="mb-4 flex w-full flex-col">
            <div
                className="flex items-center gap-2 py-2 cursor-pointer hover:bg-muted/30 rounded-md text-muted-foreground"
                onClick={() => setIsCollapsed(!isCollapsed)}
            >
                <span className={cn('text-sm flex items-center gap-2', isRunning && 'animate-pulse')}>
                    {getThinkingState()}
                </span>
                <div className="grow" />
                <motion.div animate={{ rotate: isCollapsed ? 0 : 180 }} transition={{ duration: 0.2 }}>
                    <ChevronDownIcon className="h-4 w-4" />
                </motion.div>
            </div>
            <motion.div
                initial={false}
                animate={{ height: isCollapsed ? 0 : 'auto', opacity: isCollapsed ? 0 : 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                style={{ overflow: 'hidden' }}
                className="border-l-2 ml-6 mt-1"
            >
                <div className="px-4 text-sm text-muted-foreground whitespace-pre-line">
                    {prevText}
                    {renderedDeltas.map((delta, i) => (
                        <span key={i} className="animate-fade-in-0.2">
                            {delta}
                        </span>
                    ))}
                </div>
            </motion.div>
        </div>
    );
};

namespace AssistantMessageContent {
    export type Element = HTMLDivElement;
    export type Props = MessagePrimitive.Content.Props & ComponentPropsWithoutRef<'div'>;
}

const AssistantMessageContent = forwardRef<AssistantMessageContent.Element, AssistantMessageContent.Props>(
    ({ components: componentsProp, ...rest }, ref) => {
        const { tools, assistantMessage: { components = {} } = {} } = useThreadConfig();

        const toolsComponents = useMemo(
            () => ({
                by_name: !tools
                    ? undefined
                    : Object.fromEntries(tools.map((t) => [t.unstable_tool.toolName, t.unstable_tool.render])),
                Fallback: components.ToolFallback,
            }),
            [...(tools ?? []), components.ToolFallback]
        );
        const Footer = components.Footer;

        return (
            <AssistantMessageContentWrapper {...rest} ref={ref}>
                <MessagePrimitive.Content
                    components={{
                        ...componentsProp,
                        Text: componentsProp?.Text ?? components.Text ?? ContentPart.Text,
                        Empty: componentsProp?.Empty ?? components.Empty,
                        Reasoning: componentsProp?.Reasoning ?? ReasoningPreview,
                        tools: toolsComponents,
                    }}
                />
                {Footer && <Footer />}
            </AssistantMessageContentWrapper>
        );
    }
);

AssistantMessageContent.displayName = 'AssistantMessageContent';

const exports = {
    Root: AssistantMessageRoot,
    Content: AssistantMessageContent,
};

export default Object.assign(AssistantMessage, exports) as typeof AssistantMessage & typeof exports;
